import { Feature, Map as OlMap, View } from 'ol';
import { Geometry } from 'ol/geom';
import TileLayer from 'ol/layer/Tile';
import { XYZ } from 'ol/source';
import IGC from 'ol/format/IGC.js';
import KML from 'ol/format/KML.js';
import GPX from 'ol/format/GPX.js';
import TopoJSON from 'ol/format/TopoJSON.js';
import GeoJSON from 'ol/format/GeoJSON.js';
import { unzipSync } from 'fflate';
import { environment } from 'src/environments/environment';
import { transform } from 'ol/proj';
import { inferType } from 'src/app/utils/string.utils';
import { SafeHtml } from '@angular/platform-browser';
import { defaults as defaultInteractions } from 'ol/interaction';
import { ImportWizardStorage, TabularColumns } from '../../../../import-wizard.storage';
import { ImportWizardGeoSpatialService } from '../../import-wizard-geospatial.service';
import { firstValueFrom } from 'rxjs';
import { computed, inject, Injectable, signal } from '@angular/core';

interface FeatureInfo {
    description?: string | SafeHtml;
    name?: string;
    _name?: string;
    layer?: string;
    properties: Record<string, any>;
}

// KMZ Format Class
export class KMZ extends KML {
    private zip: Record<string, Uint8Array> = {};
    private imageUrlCache = new Map<string, string>();

    constructor(opt_options?: any) {
        const options = opt_options || {};
        options.iconUrlFunction = (href: string) => this.getKMLImage(href);
        super(options);
    }

    private getKMLData(buffer: ArrayBuffer): string | null {
        this.cleanupImages();
        this.zip = unzipSync(new Uint8Array(buffer));
        const kml = Object.keys(this.zip).find((key) => /\.kml$/i.test(key));
        if (!kml || !(kml in this.zip)) return null;
        return new TextDecoder().decode(this.zip[kml]);
    }

    private getKMLImage(href: string): string {
        const index = window.location.href.lastIndexOf('/');
        if (index === -1) return href;
        const image = href.slice(index + 1);
        if (!(image in this.zip)) return href;
        const cached = this.imageUrlCache.get(image);
        if (cached) return cached;
        const data = this.zip[image];
        const safeBuffer = new Uint8Array(data).buffer;
        const url = URL.createObjectURL(new Blob([safeBuffer]));
        this.imageUrlCache.set(image, url);
        return url;
    }

    private cleanupImages(): void {
        for (const url of this.imageUrlCache.values()) {
            URL.revokeObjectURL(url);
        }
        this.imageUrlCache.clear();
    }

    override readFeature(source: any, options?: any): Feature<Geometry> {
        const kmlData = this.getKMLData(source);
        return super.readFeature(kmlData!, options);
    }

    override readFeatures(source: any, options?: any): Feature<Geometry>[] {
        const kmlData = this.getKMLData(source);
        return super.readFeatures(kmlData!, options);
    }
}

@Injectable()
export class ImportWizardGeospatialMinimapService {
    private importWizardStorage = inject(ImportWizardStorage);
    private importWizardGeospatialService = inject(ImportWizardGeoSpatialService);

    fileExtension = computed(() => {
        const name = this.importWizardStorage.file()?.name || this.importWizardStorage.state().filename || '';
        return name.split('.').pop()?.toLowerCase() || '';
    });

    map: OlMap;

    onFeaturesLoaded?: (features: Feature<Geometry>[]) => void;
    onFeaturesKMLLoaded?: (featuresFile: Feature<Geometry>[]) => void;

    // Total feature count reported by the shapefile preview API (`total_features`),
    // independent of how many features were actually returned/rendered (bounded by `limit`).
    previewTotalFeatures = signal<number | null>(null);

    // Full-dataset extent (EPSG:4326, [minx, miny, maxx, maxy]) reported by the
    // shapefile preview API, independent of how many features were sampled — lets
    // the map frame the whole area even when only a subset is drawn.
    previewBbox = signal<[number, number, number, number] | null>(null);

    satellite = new TileLayer({
        source: new XYZ({
            crossOrigin: 'Anonymous',
            url: `https://api.mapbox.com/styles/v1/${environment.mapbox.user}/satellite-streets-v12/tiles/256/{z}/{x}/{y}?access_token=${environment.mapbox.accessToken}`
        })
    });

    async retrieveFileData(): Promise<void> {
        this.initializeMap();
        await this.initializeFile();
    }

    async initializeFile(): Promise<void> {
        const ext = this.fileExtension();

        if (!this.importWizardStorage.file() && ext !== 'zip') {
            throw new Error('Nenhum arquivo selecionado');
        }

        const file = this.importWizardStorage.file() as File | null;

        // KMZ e KML gerenciam o fluxo completo internamente (mapa + tabela)
        if (ext === 'kmz' || ext === 'kml') {
            await this.readFileKML(file as File, ext);
            return;
        }

        // Demais formatos: readFile retorna features, initializeFile renderiza
        const features = await this.readFile(file);
        if (features && features.length > 0) {
            this.useFeatures(features, ext);
        }
    }

    // Fluxo exclusivo para KMZ e KML:
    // renderizacao visual via arquivo local + dados tabulares via API (ou local se MultiFeatured)
    private async readFileKML(file: File, ext: string): Promise<void> {
        const projection = this.map.getView().getProjection();

        const readFeaturesProps = {
            dataProjection: 'EPSG:4326',
            featureProjection: projection
        };

        if (ext === 'kmz') {
            const buffer = await file.arrayBuffer();
            const featuresFile = new KMZ().readFeatures(buffer, {
                featureProjection: projection
            });

            const isMultiFeatured = this.importWizardStorage.state().MultiFeatured;

            if (isMultiFeatured) {
                this.useFeaturesKML(featuresFile);
            } else {
                const geojson = await this.handleShapeFile();
                this.useFeaturesKML(featuresFile, geojson);
            }
        } else {
            const buffer = await file.arrayBuffer();
            const text = new TextDecoder().decode(buffer);
            const featuresFile = new KML().readFeatures(text, readFeaturesProps);

            const geojson = await this.handleShapeFile();
            this.useFeaturesKML(featuresFile, geojson);
        }
    }

    private async readFile(file: File | null): Promise<Feature<Geometry>[] | null> {
        const projection = this.map.getView().getProjection();
        const ext = this.fileExtension();

        const readFeaturesProps = {
            dataProjection: 'EPSG:4326',
            featureProjection: projection
        };

        // Shapefile (zip): apenas API
        if (ext === 'zip') {
            const geojson = await this.handleShapeFile();
            return new GeoJSON().readFeatures(geojson, readFeaturesProps);
        }

        if (!file) {
            throw new Error('Nenhum arquivo selecionado');
        }

        // Demais formatos: leitura local direta
        const format = this.resolveFormat(ext);
        if (!format) {
            throw new Error('Formato de arquivo nao suportado');
        }

        const buffer = await file.arrayBuffer();
        const text = new TextDecoder().decode(buffer);

        return format.readFeatures(text, {
            featureProjection: projection
        });
    }

    initializeMap(targetId = 'map'): void {
        const projected = transform([-48, -25], 'EPSG:4326', 'EPSG:3857').map(function (e) {
            return Math.round(e);
        });

        this.satellite.setVisible(true);

        this.map = new OlMap({
            layers: [this.satellite],
            target: targetId,
            view: new View({
                center: projected,
                zoom: 2,
            }),
            interactions: defaultInteractions({}),
        });
    }

    // Requests every feature in the file (bounded only by the router's sanity cap,
    // 200000). Measured against a 24k-feature real-world file: ~10.7s and ~17.6MB —
    // noticeably slower/heavier than a sampled preview, and payload scales with the
    // file, so a much larger file could get slow or risk hitting response-size
    // limits. Trying this per explicit request; revisit if it proves too slow/heavy
    // in practice.
    private static readonly SHAPEFILE_PREVIEW_LIMIT = 200000;

    private async handleShapeFile(): Promise<string> {
        const res = await firstValueFrom(
            this.importWizardGeospatialService.getImportDataPreview(
                this.importWizardStorage.state().id,
                ImportWizardGeospatialMinimapService.SHAPEFILE_PREVIEW_LIMIT
            )
        );
        this.previewTotalFeatures.set((res as any)?.total_features ?? null);
        const bbox = (res as any)?.bbox;
        this.previewBbox.set(Array.isArray(bbox) && bbox.length === 4 ? (bbox as [number, number, number, number]) : null);
        return JSON.stringify(res);
    }

    private useFeatures(features: Feature<Geometry>[], extension: string): void {
        this.extractFeatureInfo(features);
        this.onFeaturesLoaded?.(features);
    }

    // featuresFile: features para renderizacao visual no mapa
    // geojson: se fornecido, extrai dados tabulares diretamente do GeoJSON da API
    //          se omitido (MultiFeatured), extrai das features locais
    private useFeaturesKML(featuresFile: Feature<Geometry>[], geojson?: string): void {
        if (geojson) {
            this.extractTabularFromGeoJSON(geojson);
        } else {
            this.extractFeatureInfo(featuresFile);
        }
        this.onFeaturesKMLLoaded?.(featuresFile);
    }

    // Extrai colunas e rows diretamente das properties do GeoJSON cru da API,
    // sem passar pelo OpenLayers (que adiciona styleUrl, pdfmaps_photos, etc.)
    public extractTabularFromGeoJSON(geojson: string): void {
        const parsed = JSON.parse(geojson);
        const features: any[] = parsed?.features ?? [];

        if (features.length === 0) {
            this.importWizardStorage.setColumns([]);
            this.importWizardStorage.setTabularRows([]);
            this.importWizardStorage.setFileTotalRows(0);
            return;
        }

        const firstProps = features[0].properties ?? {};
        const columns: TabularColumns[] = Object.entries(firstProps).map(([key, value]) => ({
            name: key,
            type: inferType(value as string)
        }));

        const rows = features.map(f => f.properties ?? {});

        this.importWizardStorage.setColumns(columns);
        this.importWizardStorage.setTabularRows(rows);
        this.importWizardStorage.setFileTotalRows(parsed.total_features ?? rows.length);
    }

    private readonly KML_INTERNAL_KEYS = new Set([
        'geometry', 'description', 'name', '_name', 'layer',
        'styleUrl', 'Style', 'styleMapUrl', 'pdfmaps_photos', 'pdfmaps_photo', 'snippet', 'altitudeMode', 'drawOrder',
    ]);

    private extractFeatureInfo(features: Feature<Geometry>[]): void {
        const info: FeatureInfo[] = features.map(feature => {
            const properties = feature.getProperties();
            const rawDescription = feature.get('description');

            return {
                description: rawDescription,
                name: feature.get('name'),
                _name: feature.get('_name'),
                layer: feature.get('layer'),
                properties: Object.fromEntries(
                    Object.entries(properties).filter(([key]) =>
                        !this.KML_INTERNAL_KEYS.has(key)
                    )
                )
            };
        });

        const columns: TabularColumns[] = info.length
            ? Object.entries(info[0].properties).map(([key, value]) => ({
                name: key,
                type: inferType(value)
            }))
            : [];

        const rows = info.map(item => item.properties);

        this.importWizardStorage.setColumns(columns);
        this.importWizardStorage.setTabularRows(rows);
        this.importWizardStorage.setFileTotalRows(rows.length);
    }

    private resolveFormat(extension?: string): GPX | GeoJSON | IGC | TopoJSON | null {
        switch (extension) {
            case 'gpx':      return new GPX();
            case 'geojson':
            case 'json':     return new GeoJSON();
            case 'igc':      return new IGC();
            case 'topojson': return new TopoJSON();
            default:         return null;
        }
    }
}
